import React, { useState } from "react";
import { useQuery } from "react-query";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { Bars } from "react-loader-spinner";
import axiosInstance from "../../../Shared/axiosInstance";
import CustomDatePicker from "../../CustomDate/CustomDatePicker";

export default function SupplierBalanceOpenSummary() {
  const navigate = useNavigate();

  // Filter & pagination state
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [supplierSearch, setSupplierSearch] = useState("");

  // ✅ Fetch Suppliers
  const {
    data: suppliersData,
    isLoading: suppliersLoading,
    isError: suppliersError,
    refetch: refetchSuppliers,
  } = useQuery(
    ["suppliers", supplierSearch],
    async () => {
      const { data } = await axiosInstance.get("/Supplier/all", {
        params: {
          name: supplierSearch || undefined,
          pageNumber: 1,
          pageSize: 15, // جلب الموردين دفعة واحدة
        }
      });
      return data.data || [];
    },
    {
      staleTime: 1000 * 60 * 5,
      keepPreviousData: true,
    }
  );

  // ✅ Fetch Supplier Opening Balances with filters & pagination
  const {
    data: balancesData,
    isLoading: balancesLoading,
    isError: balancesError,
    refetch: refetchBalances,
  } = useQuery(
    [
      "supplier-opening-balances",
      currentPage,
      pageSize,
      selectedSupplier,
      startDate,
      endDate,
    ],
    async () => {
      const params = new URLSearchParams();
      params.append("pageNumber", currentPage);
      params.append("pageSize", pageSize);
      if (startDate) params.append("startDate", startDate);
      if (endDate) params.append("endDate", endDate || startDate);
      if (selectedSupplier) params.append("supplierId", selectedSupplier);

      const { data } = await axiosInstance.get(`/SupplierOpeningBalance?${params.toString()}`);
      return data;
    },
    {
      keepPreviousData: true,
    }
  );

  const handleFilter = () => {
    if (!startDate && !endDate && !selectedSupplier) {
      Swal.fire("تحذير", "يرجى اختيار فلتر واحد على الأقل", "warning");
      return;
    }
    setCurrentPage(1);
    refetchBalances();
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setSelectedSupplier("");
    setSupplierSearch("");
    setCurrentPage(1);
    refetchBalances();
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > (balancesData?.totalPages || 1)) return;
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const getPageNumbers = () => {
    const totalPages = balancesData?.totalPages || 1;
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      const currentPageSafe = currentPage;
      if (currentPageSafe <= 3) pages.push(1, 2, 3, 4, '...', totalPages);
      else if (currentPageSafe >= totalPages - 2)
        pages.push(1, '...', totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      else pages.push(1, '...', currentPageSafe - 1, currentPageSafe, currentPageSafe + 1, '...', totalPages);
    }
    return pages;
  };

  if (balancesLoading || suppliersLoading) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <Bars height="80" width="80" color="#2563eb" ariaLabel="loading" />
        <p className="mt-3" style={{ color: "#64748b", fontSize: "18px" }}>
          جاري تحميل البيانات...
        </p>
      </div>
    );
  }

  if (balancesError || suppliersError) {
    return (
      <div className="vh-100 d-flex align-items-center justify-content-center flex-column">
        <p style={{ color: "#dc2626", fontSize: "18px" }}>فشل تحميل البيانات</p>
        <button
          onClick={() => { refetchBalances(); refetchSuppliers(); }}
          style={{ ...buttonStyle("#2563eb") }}
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }

  const balances = balancesData?.data || [];
  const totalCount = balancesData?.totalCount || 0;
  const grandTotals = balancesData?.grandTotals || { totalAmount: 0, totalPaid: 0, totalRemaining: 0 };
  const totalPages = balancesData?.totalPages || 1;
  const hasNext = balancesData?.hasNext || false;
  const hasPrevious = balancesData?.hasPrevious || false;

  return (
    <div className="container py-5" style={{ maxWidth: "1400px" }}>
      {/* رأس الصفحة */}
      <div className="d-flex justify-content-between align-items-center mb-4 mt-5 pt-5">
        <div>
          <h2 style={{ color: "#2563eb", fontWeight: "700", fontSize: "28px", marginBottom: "4px" }}>
            ملخص الأرصدة الافتتاحية للموردين
          </h2>
          <small style={{ color: "#64748b", fontSize: "14px" }}>
            عرض {balances.length} من {totalCount} رصيد افتتاحي
          </small>
        </div>
        <button
          onClick={() => navigate("/open-balance-suppliers")}
          style={{
            background: "#22c55e",
            color: "#fff",
            padding: "10px 20px",
            borderRadius: "6px",
            border: "none",
            fontWeight: "600",
            fontSize: "15px",
            boxShadow: "0 2px 8px rgba(34, 197, 94, 0.3)",
            transition: "all 0.3s ease",
            cursor: "pointer",
          }}
        >
          <i className="bi bi-plus-circle me-1"></i> إضافة رصيد افتتاحي
        </button>
      </div>

      {/* الفلاتر */}
      <div className="mb-4" style={{ background: "#fff", border: "1px solid #dbeafe", borderRadius: "8px", padding: "20px", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)" }}>
        <div className="row g-3 align-items-end">
          {/* 👇 البحث عن المورد */}
          <div className="col-md-3">
            <label style={labelStyle}>البحث عن المورد</label>
            <input
              type="text"
              placeholder="🔍 اكتب اسم المورد..."
              value={supplierSearch}
              onChange={(e) => setSupplierSearch(e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* 👇 اختيار المورد */}
          <div className="col-md-3">
            <label style={labelStyle}>المورد</label>
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              style={inputStyle}
            >
              <option value="">كل الموردين</option>
              {suppliersLoading ? (
                <option disabled>جارٍ التحميل...</option>
              ) : suppliersData && suppliersData.length > 0 ? (
                suppliersData.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} {s.details}</option>
                ))
              ) : (
                <option disabled>لم يتم العثور على موردين</option>
              )}
            </select>
          </div>

          <div className="col-md-2">
            <CustomDatePicker
              label="تاريخ البداية"
              value={startDate}
              onChange={setStartDate}
              placeholder="اختر تاريخ البداية"
              maxDate={endDate ? new Date(endDate) : null}
            />
          </div>

          <div className="col-md-2">
            <CustomDatePicker
              label="تاريخ النهاية"
              value={endDate}
              onChange={setEndDate}
              placeholder="اختر تاريخ النهاية"
              minDate={startDate ? new Date(startDate) : null}
            />
          </div>

          <div className="col-md-2">
            <label style={labelStyle}>عدد العناصر في الصفحة</label>
            <select value={pageSize} onChange={(e) => handlePageSizeChange(Number(e.target.value))} style={inputStyle}>
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>
          </div>
          <div className="col-md-3 d-flex gap-2">
            <button onClick={handleFilter} style={buttonStyle("#2563eb")}>تطبيق الفلتر</button>
            <button onClick={handleReset} style={buttonStyle("#64748b")}>إعادة تعيين</button>
          </div>
        </div>
      </div>

      {/* بطاقات الملخص */}
      <div className="row g-3 mb-4">
        {["totalAmount", "totalPaid", "totalRemaining"].map((key, idx) => {
          const colors = ["#22c55e", "#2563eb", "#dc2626"];
          const borderColors = ["#22c55e", "#2563eb", "#dc2626"];
          const labels = ["إجمالي المبلغ", "إجمالي المدفوع", "إجمالي المتبقي"];
          return (
            <div key={idx} className="col-md-4">
              <div style={{ background: "#fff", border: `2px solid ${borderColors[idx]}`, borderRadius: "8px", padding: "20px", textAlign: "center", boxShadow: `0 4px 12px ${colors[idx]}33` }}>
                <h6 style={{ color: "#64748b", fontSize: "13px", fontWeight: "600", textTransform: "uppercase", marginBottom: "10px" }}>
                  {labels[idx]}
                </h6>
                <h3 style={{ color: colors[idx], fontSize: "28px", fontWeight: "700", marginBottom: "4px" }}>
                  {grandTotals[key]?.toFixed(2)} جنيه
                </h3>
                <small style={{ color: "#64748b", fontSize: "13px" }}>({totalCount} رصيد افتتاحي)</small>
              </div>
            </div>
          );
        })}
      </div>

      {/* جدول الأرصدة */}
      <div className="table-responsive">
        <table className="table mb-0" style={{ border: "1px solid #dbeafe", borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 12px rgba(59, 130, 246, 0.15)", textAlign: "center" }}>
          <thead style={{ background: "#dbeafe" }}>
            <tr>
              <th style={thStyle}>المورد</th>
              <th style={thStyle}>التاريخ</th>
              <th style={thStyle}>إجمالي المبلغ</th>
              <th style={thStyle}>المدفوع</th>
              <th style={thStyle}>المتبقي</th>
              <th style={thStyle}>الحالة</th>
              <th style={thStyle}>الإجراءات</th>
            </tr>
          </thead>
          <tbody>
            {balances.length > 0 ? (
              balances.map((balance, index) => (
                <tr key={balance.id} style={{ background: index % 2 === 0 ? "#f8fafc" : "#ffffff" }}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{balance.supplierName}</td>
                  <td style={{ ...tdStyle, fontSize: "17px" }}>{new Date(balance.balanceDate).toLocaleDateString()}</td>
                  <td style={{ ...tdStyle, color: "#22c55e", fontWeight: 600 }}>{balance.totalAmount.toFixed(2)}</td>
                  <td style={{ ...tdStyle, color: "#2563eb", fontWeight: 600 }}>{balance.paidAmount.toFixed(2)}</td>
                  <td style={{ ...tdStyle, color: "#dc2626", fontWeight: 600 }}>{balance.remainingAmount.toFixed(2)}</td>
                  
                  <td style={tdStyle}>
                    <span style={{
                      background: balance.paymentStatus === "Paid" ? "#bbf7d0" : balance.paymentStatus === "PartiallyPaid" ? "#fef3c7" : "#fecaca",
                      color: balance.paymentStatus === "Paid" ? "#14532d" : balance.paymentStatus === "PartiallyPaid" ? "#92400e" : "#991b1b",
                      padding: "4px 10px",
                      borderRadius: "4px",
                      fontSize: "16px",
                      fontWeight: "600"
                    }}>
                      {balance.paymentStatus === "Paid"
                        ? "مدفوع"
                        : balance.paymentStatus === "PartiallyPaid"
                        ? "مدفوع جزئي"
                        : "غير مدفوع"}
                    </span>
                  </td>

                  <td style={tdStyle}>
                    <div className="d-flex gap-2 justify-content-center flex-wrap">
                      <button style={actionBtnStyle("#3b82f6")} onClick={() => navigate(`/supplier-opening-balance/${balance.id}`)}>التفاصيل</button>
                      <button style={actionBtnStyle("#22c55e")} onClick={() => navigate(`/supplier-opening-balance/${balance.id}/add-payment`)}>الدفع</button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" style={{ ...tdStyle, color: "#64748b", padding: "25px" }}>
                  لا توجد أرصدة افتتاحية
                  <div className="mt-3">
                    <button onClick={() => navigate("/open-balance-suppliers")} style={{ background: "#22c55e", color: "#fff", padding: "8px 16px", borderRadius: "6px", border: "none", fontWeight: "600", fontSize: "14px" }}>إضافة أول رصيد افتتاحي</button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* الترقيم */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div style={{ color: "#64748b", fontSize: "14px" }}>
            الصفحة {currentPage} من {totalPages}
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${!hasPrevious ? 'disabled' : ''}`}>
                <button onClick={() => handlePageChange(currentPage - 1)} style={{ ...paginationBtnStyle, cursor: !hasPrevious ? "not-allowed" : "pointer", opacity: !hasPrevious ? 0.5 : 1 }}>السابق</button>
              </li>
              {getPageNumbers().map((page, idx) => (
                <li key={idx}>
                  {page === '...' ? <span style={{ ...paginationBtnStyle, cursor: "default" }}>...</span> : <button onClick={() => handlePageChange(page)} style={{ ...paginationBtnStyle, background: currentPage === page ? "#2563eb" : "#fff", color: currentPage === page ? "#fff" : "#475569", fontWeight: currentPage === page ? "600" : "500" }}>{page}</button>}
                </li>
              ))}
              <li className={`page-item ${!hasNext ? 'disabled' : ''}`}>
                <button onClick={() => handlePageChange(currentPage + 1)} style={{ ...paginationBtnStyle, cursor: !hasNext ? "not-allowed" : "pointer", opacity: !hasNext ? 0.5 : 1 }}>التالي</button>
              </li>
            </ul>
          </nav>
        </div>
      )}
    </div>
  );
}

// ✅ Styles
const thStyle = { color: "#1e40af", fontSize: "14px", padding: "12px 8px", fontWeight: "600", border: "1px solid #bfdbfe" };
const tdStyle = { padding: "12px 8px", border: "1px solid #e0e7ff", fontSize: "19px", color: "#475569", fontWeight: "600" };
const labelStyle = { color: "#475569", fontSize: "14px", fontWeight: "600", marginBottom: "6px", display: "block" };
const inputStyle = { width: "100%", padding: "10px 12px", border: "1px solid #bfdbfe", borderRadius: "6px", fontSize: "15px", color: "#475569", outline: "none", cursor: "pointer", background: "#fff" };
const buttonStyle = (bgColor) => ({ background: bgColor, color: "#fff", padding: "10px 16px", borderRadius: "6px", border: "none", fontWeight: "600", fontSize: "14px", cursor: "pointer", transition: "all 0.3s ease" });
const actionBtnStyle = (color) => ({ background: color, color: "#fff", padding: "5px 10px", borderRadius: "6px", border: "none", fontWeight: "600", fontSize: "17px", cursor: "pointer", transition: "all 0.3s ease", whiteSpace: "nowrap" });
const paginationBtnStyle = { padding: "8px 14px", margin: "0 3px", border: "1px solid #bfdbfe", borderRadius: "6px", fontSize: "14px", cursor: "pointer", transition: "all 0.2s ease" };